import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
    ArrowRight, Check, Dumbbell, Flame, HeartPulse, Trophy,
    Brain, Calendar, MessageSquare, Utensils, Star, Loader2,
    ShieldCheck, Sparkles, Quote, Menu, X,
} from "lucide-react";

// =====================================================================
// NEXT LEVEL BODY LAB — Premium landing page
// Dark matte aesthetic + deep gold accents
// =====================================================================

const PHASES = [
    {
        n: "01",
        title: "Foundation",
        weeks: "Weeks 1–4",
        icon: ShieldCheck,
        copy: "Movement quality, baseline metrics, and the habits that make every future block work. We fix what's leaking before we add load.",
    },
    {
        n: "02",
        title: "Build",
        weeks: "Weeks 5–10",
        icon: Dumbbell,
        copy: "Hypertrophy programming dialed to your leverages. Compound strength + targeted accessory work — the size phase that doesn't smoke your joints.",
    },
    {
        n: "03",
        title: "Burn",
        weeks: "Weeks 11–14",
        icon: Flame,
        copy: "Strategic conditioning + macro pivots to reveal the muscle you just built. Cardio that doesn't kill performance.",
    },
    {
        n: "04",
        title: "Peak",
        weeks: "Weeks 15–16",
        icon: Trophy,
        copy: "Final polish. Photo-ready protocols, lifestyle plug-ins, and a maintenance plan so the work sticks long after week 16.",
    },
];

const BENEFITS = [
    { icon: Brain, title: "Built for your goal", copy: "Hypertrophy, fat loss, rehab, performance — every program is engineered around your single most important outcome." },
    { icon: Calendar, title: "Day-by-day clarity", copy: "Workouts auto-scheduled to your calendar. M/W/F splits, drag-drop tweaks, interval timer built in." },
    { icon: Utensils, title: "Nutrition that fits", copy: "Custom meal plans with macro targets, swap-friendly food library, and weekly check-ins." },
    { icon: MessageSquare, title: "Coach in your pocket", copy: "Direct messaging with King. 24h replies on Pro, 8h on Elite. Form-check videos, weekly feedback." },
    { icon: Sparkles, title: "Progress you can see", copy: "Streaks, photo timeline, weight & strength charts. Proof of work, not vibes." },
    { icon: HeartPulse, title: "Sustainable forever", copy: "No crash cuts, no bro-science. Programs you can actually run again next year." },
];

const TESTIMONIALS = [
    {
        name: "Marcus T.",
        role: "Lost 38 lbs · Gained back-squat 90 lbs",
        quote: "I'd been stuck for two years. Eight weeks in and I'm hitting numbers I never thought my body could. The plan is personal — it actually fits the way I live.",
        rating: 5,
    },
    {
        name: "Jasmine R.",
        role: "Postpartum rebuild · 16 weeks",
        quote: "King didn't just give me workouts. He gave me back my confidence. Every check-in moved me forward, and the nutrition piece finally clicked.",
        rating: 5,
    },
    {
        name: "Devon K.",
        role: "Bodybuilding prep · 6-month transformation",
        quote: "Coach is locked in. Programming is sharp, the feedback is honest, and the dashboard makes everything trackable. I trust this system completely.",
        rating: 5,
    },
];

const PLAN_LABELS = {
    starter: { tag: "Programming + direction", popular: false },
    pro:     { tag: "The full transformation engine", popular: true },
    elite:   { tag: "1-on-1 with King", popular: false },
};

export default function LandingPage() {
    const [plans, setPlans] = useState([]);
    const [navOpen, setNavOpen] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/plans").then(({ data }) => setPlans(data)).catch(() => setPlans([]));
    }, []);

    const scrollTo = (id) => {
        setNavOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="landing-page">
            {/* ---------- NAV ---------- */}
            <header className="sticky top-0 z-40 bg-[var(--bg)]/85 backdrop-blur-md border-b border-[var(--border)]">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button onClick={() => scrollTo("hero")} className="text-left" data-testid="landing-brand" aria-label="Next Level Body Lab">
                        <div className="font-serif text-xl tracking-[0.32em] gold-gradient-text leading-none" aria-hidden="true">NEXT LEVEL</div>
                        <div className="font-display text-[10px] tracking-[0.4em] text-[var(--text-muted)] mt-1 leading-none" aria-hidden="true">BODY LAB</div>
                    </button>
                    <nav className="hidden md:flex items-center gap-8 text-sm font-mono tracking-[0.15em] uppercase text-[var(--text-muted)]">
                        <button onClick={() => scrollTo("about")} className="hover:text-[var(--gold-bright)]" data-testid="nav-about">About</button>
                        <button onClick={() => scrollTo("phases")} className="hover:text-[var(--gold-bright)]" data-testid="nav-phases">Program</button>
                        <button onClick={() => scrollTo("benefits")} className="hover:text-[var(--gold-bright)]" data-testid="nav-benefits">Why</button>
                        <button onClick={() => scrollTo("pricing")} className="hover:text-[var(--gold-bright)]" data-testid="nav-pricing">Pricing</button>
                        <button onClick={() => scrollTo("apply")} className="hover:text-[var(--gold-bright)]" data-testid="nav-apply">Apply</button>
                    </nav>
                    <div className="hidden md:flex items-center gap-3">
                        <Link to="/login" className="btn-ghost text-xs" data-testid="landing-login">Sign in</Link>
                        <button onClick={() => scrollTo("apply")} className="btn-primary text-xs" data-testid="landing-cta-top">
                            Start now
                        </button>
                    </div>
                    <button onClick={() => setNavOpen(!navOpen)} className="md:hidden p-2 -mr-2 text-[var(--text)]" aria-label="Menu" data-testid="landing-mobile-menu">
                        {navOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
                {navOpen && (
                    <div className="md:hidden border-t border-[var(--border)] bg-[var(--bg)]">
                        <div className="px-6 py-4 flex flex-col gap-3 text-sm font-mono tracking-[0.15em] uppercase">
                            <button onClick={() => scrollTo("about")} className="text-left py-1 text-[var(--text-muted)]">About</button>
                            <button onClick={() => scrollTo("phases")} className="text-left py-1 text-[var(--text-muted)]">Program</button>
                            <button onClick={() => scrollTo("benefits")} className="text-left py-1 text-[var(--text-muted)]">Why</button>
                            <button onClick={() => scrollTo("pricing")} className="text-left py-1 text-[var(--text-muted)]">Pricing</button>
                            <button onClick={() => scrollTo("apply")} className="text-left py-1 text-[var(--text-muted)]">Apply</button>
                            <div className="flex gap-3 pt-2 border-t border-[var(--border)] mt-2">
                                <Link to="/login" className="btn-ghost text-xs flex-1 text-center">Sign in</Link>
                                <button onClick={() => scrollTo("apply")} className="btn-primary text-xs flex-1">Start</button>
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* ---------- HERO ---------- */}
            <section id="hero" className="relative overflow-hidden border-b border-[var(--border)]">
                <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
                <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--gold)] opacity-[0.04] blur-[120px] pointer-events-none" />
                <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--gold-bright)] opacity-[0.03] blur-[120px] pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-36 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--gold-deep)] bg-[var(--surface)]/60 mb-8">
                            <Sparkles size={12} className="text-[var(--gold-bright)]" />
                            <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[var(--gold-bright)]">Now accepting Q1 clients</span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-8">
                            Build the body<br />
                            you were <span className="gold-gradient-text italic">supposed</span> to have.
                        </h1>
                        <p className="text-base lg:text-lg text-[var(--text-muted)] leading-relaxed max-w-xl mb-10">
                            Personalized training, nutrition, and accountability — engineered by King Seidorb.
                            A 16-week system designed to take you from &quot;trying&quot; to <span className="text-[var(--text)]">undeniable</span>.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button onClick={() => scrollTo("apply")} className="btn-primary inline-flex items-center justify-center gap-2 text-sm" data-testid="hero-cta-primary">
                                Apply for coaching <ArrowRight size={14} />
                            </button>
                            <button onClick={() => scrollTo("phases")} className="btn-ghost inline-flex items-center justify-center gap-2 text-sm" data-testid="hero-cta-secondary">
                                See the program
                            </button>
                        </div>
                        <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs font-mono tracking-[0.18em] uppercase text-[var(--text-muted)]">
                            <span className="flex items-center gap-2"><Check size={12} className="text-[var(--gold-bright)]" /> 7-day free trial</span>
                            <span className="flex items-center gap-2"><Check size={12} className="text-[var(--gold-bright)]" /> No card required</span>
                            <span className="flex items-center gap-2"><Check size={12} className="text-[var(--gold-bright)]" /> Cancel anytime</span>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5] bg-[var(--surface)] border border-[var(--border-bright)] overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-[var(--gold-deep)]/20 via-transparent to-[var(--bg-deep)]" />
                            <div className="absolute inset-0 bg-stripes opacity-30" />
                            <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-10">
                                <p className="label-tiny mb-2 text-[var(--gold-bright)]">/ The promise</p>
                                <p className="font-serif text-3xl lg:text-4xl leading-tight">
                                    16 weeks. One coach.<br />
                                    A version of you that <span className="gold-gradient-text italic">won&apos;t be denied</span>.
                                </p>
                            </div>
                            <div className="absolute top-6 right-6 text-right">
                                <p className="font-display text-[10rem] leading-none gold-gradient-text opacity-20">16</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- ABOUT ---------- */}
            <section id="about" className="border-b border-[var(--border)]">
                <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <p className="label-tiny mb-4 text-[var(--gold-bright)]">/ About King</p>
                        <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] mb-6">
                            Built by a coach who&apos;s <span className="italic">actually been in the room</span>.
                        </h2>
                        <div className="gold-rule max-w-xs" />
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-[var(--text-muted)] leading-relaxed">
                        <p>
                            <span className="text-[var(--text)]">King Seidorb</span> has spent over a decade studying — and producing — physical transformations.
                            Athletes, executives, postpartum rebuilders, lifelong &quot;skinny guys&quot;, and people who&apos;d written their bodies off.
                        </p>
                        <p>
                            Next Level Body Lab is the system he wishes he&apos;d had as a client. Programming that adapts. Nutrition that respects your life.
                            A coach who answers. And a dashboard that turns the chaos of &quot;getting in shape&quot; into a daily clear next step.
                        </p>
                        <p>
                            <span className="text-[var(--gold-bright)]">No gimmicks. No 30-day miracles. No supplements you&apos;ll have to buy from him.</span>
                            Just the kind of programming that, when you follow it, makes your old self look like a rough draft.
                        </p>
                        <div className="grid grid-cols-3 gap-6 pt-6 border-t border-[var(--border)]">
                            <Stat n="1,200+" label="Bodies transformed" />
                            <Stat n="10 yrs" label="Coaching" />
                            <Stat n="98%" label="Finish rate" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- 4 PHASES ---------- */}
            <section id="phases" className="border-b border-[var(--border)] bg-[var(--bg-deep)]/40">
                <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
                    <div className="max-w-3xl mb-16">
                        <p className="label-tiny mb-4 text-[var(--gold-bright)]">/ The 16-week system</p>
                        <h2 className="font-serif text-4xl lg:text-6xl leading-[1] mb-6">
                            Four phases.<br />
                            One <span className="gold-gradient-text italic">undeniable</span> result.
                        </h2>
                        <p className="text-[var(--text-muted)] max-w-xl">
                            Every program follows the same architecture — but every block, every meal, every rep target is dialed to <span className="text-[var(--text)]">you</span>.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)]" data-testid="phases-grid">
                        {PHASES.map((p) => (
                            <div key={p.n} className="bg-[var(--surface)] p-8 lg:p-10 hover:bg-[var(--surface-hover)] transition-colors group" data-testid={`phase-${p.n}`}>
                                <div className="flex items-start justify-between mb-6">
                                    <p className="font-display text-6xl gold-gradient-text opacity-80 group-hover:opacity-100 transition-opacity">{p.n}</p>
                                    <p.icon size={22} className="text-[var(--gold)] mt-3" />
                                </div>
                                <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-[var(--gold-bright)] mb-2">{p.weeks}</p>
                                <h3 className="font-serif text-3xl mb-4">{p.title}</h3>
                                <p className="text-sm text-[var(--text-muted)] leading-relaxed">{p.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- BENEFITS ---------- */}
            <section id="benefits" className="border-b border-[var(--border)]">
                <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
                    <div className="grid lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <p className="label-tiny mb-4 text-[var(--gold-bright)]">/ Why it works</p>
                            <h2 className="font-serif text-4xl lg:text-6xl leading-[1]">
                                Built for the <span className="italic">actual</span> life you live.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 self-end">
                            <p className="text-[var(--text-muted)] leading-relaxed">
                                Generic plans break the moment life happens. Next Level Body Lab is engineered for what you actually do —
                                with a coach who adjusts in real time and a dashboard that keeps you on track even on the bad weeks.
                            </p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
                        {BENEFITS.map((b, i) => (
                            <div key={i} className="bg-[var(--surface)] p-8 lg:p-10 hover:bg-[var(--surface-hover)] transition-colors" data-testid={`benefit-${i}`}>
                                <div className="w-11 h-11 border border-[var(--gold-deep)] flex items-center justify-center mb-6">
                                    <b.icon size={18} className="text-[var(--gold-bright)]" />
                                </div>
                                <h3 className="font-serif text-2xl mb-3">{b.title}</h3>
                                <p className="text-sm text-[var(--text-muted)] leading-relaxed">{b.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- TESTIMONIALS ---------- */}
            <section className="border-b border-[var(--border)] bg-[var(--bg-deep)]/40">
                <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
                    <div className="max-w-3xl mb-16">
                        <p className="label-tiny mb-4 text-[var(--gold-bright)]">/ Receipts</p>
                        <h2 className="font-serif text-4xl lg:text-6xl leading-[1]">
                            Real clients.<br />
                            Real <span className="gold-gradient-text italic">work</span>. Real results.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-px bg-[var(--border)]" data-testid="testimonials">
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} className="bg-[var(--surface)] p-8 lg:p-10 flex flex-col" data-testid={`testimonial-${i}`}>
                                <Quote size={28} className="text-[var(--gold)] mb-6" />
                                <p className="text-base lg:text-lg leading-relaxed mb-8 flex-1 text-[var(--text)]">&ldquo;{t.quote}&rdquo;</p>
                                <div className="flex items-center gap-1 mb-4">
                                    {Array.from({ length: t.rating }).map((_, k) => (
                                        <Star key={k} size={12} fill="currentColor" className="text-[var(--gold-bright)]" />
                                    ))}
                                </div>
                                <div className="pt-4 border-t border-[var(--border)]">
                                    <p className="font-serif text-xl">{t.name}</p>
                                    <p className="text-xs font-mono tracking-[0.12em] uppercase text-[var(--text-muted)] mt-1">{t.role}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- PRICING ---------- */}
            <section id="pricing" className="border-b border-[var(--border)]">
                <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
                    <div className="max-w-3xl mb-16">
                        <p className="label-tiny mb-4 text-[var(--gold-bright)]">/ Pricing</p>
                        <h2 className="font-serif text-4xl lg:text-6xl leading-[1] mb-6">
                            Three ways in.<br />
                            One <span className="italic">commitment</span>.
                        </h2>
                        <p className="text-[var(--text-muted)] max-w-xl">
                            Start with a 7-day free trial. Pay monthly through PayPal. Cancel any time, no penalty — the bodies that last are built on choice, not contracts.
                        </p>
                    </div>
                    <div className="grid lg:grid-cols-3 gap-px bg-[var(--border)]" data-testid="pricing-grid">
                        {plans.length === 0 ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="bg-[var(--surface)] p-10 h-[520px] animate-pulse" />
                            ))
                        ) : plans.map((p) => {
                            const meta = PLAN_LABELS[p.id] || {};
                            const popular = meta.popular;
                            return (
                                <div key={p.id}
                                    className={`relative bg-[var(--surface)] p-8 lg:p-10 flex flex-col ${popular ? "ring-1 ring-[var(--gold)]" : ""}`}
                                    data-testid={`pricing-card-${p.id}`}>
                                    {popular && (
                                        <div className="absolute -top-px right-8 bg-[var(--gold)] text-[#0a0908] px-3 py-1 text-[10px] font-mono tracking-[0.25em] uppercase">
                                            Most chosen
                                        </div>
                                    )}
                                    <p className="label-tiny mb-3 text-[var(--gold-bright)]">/ {meta.tag || p.tagline}</p>
                                    <h3 className="font-serif text-4xl mb-2">{p.name}</h3>
                                    <div className="flex items-baseline gap-2 mt-2 mb-1">
                                        <span className="font-display text-6xl">${Math.floor(p.amount)}</span>
                                        <span className="font-display text-3xl text-[var(--text-muted)]">
                                            .{String(p.amount).split(".")[1] || "00"}
                                        </span>
                                    </div>
                                    <p className="text-xs text-[var(--text-muted)] font-mono mb-8">/ {p.interval} · PayPal</p>
                                    <ul className="space-y-3 mb-10 flex-1">
                                        {p.features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm">
                                                <Check size={14} className="text-[var(--gold-bright)] mt-1 flex-shrink-0" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => nav(`/register?trial=${p.id}&next=/pricing`)}
                                        className={`w-full inline-flex items-center justify-center gap-2 py-4 font-display tracking-[0.2em] uppercase text-sm transition-colors ${
                                            popular
                                                ? "bg-[var(--gold)] text-[#0a0908] hover:bg-[var(--gold-bright)]"
                                                : "border border-[var(--border-bright)] hover:border-[var(--gold)] hover:text-[var(--gold-bright)]"
                                        }`}
                                        data-testid={`pricing-cta-${p.id}`}
                                    >
                                        Start free trial <ArrowRight size={14} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-center text-xs text-[var(--text-muted)] font-mono mt-10 tracking-[0.18em] uppercase">
                        7-day free trial · No card required · PayPal billing after trial
                    </p>
                </div>
            </section>

            {/* ---------- LEAD CAPTURE ---------- */}
            <section id="apply" className="border-b border-[var(--border)] bg-[var(--bg-deep)]">
                <div className="max-w-5xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <p className="label-tiny mb-4 text-[var(--gold-bright)]">/ Apply</p>
                        <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] mb-6">
                            Tell us your goal.<br />
                            <span className="italic">We&apos;ll handle the rest.</span>
                        </h2>
                        <p className="text-[var(--text-muted)] leading-relaxed mb-8">
                            Drop your details and King will personally review your fit within 24 hours.
                            No spam, no auto-replies — just a real conversation about whether Next Level Body Lab is right for you.
                        </p>
                        <div className="space-y-3 text-sm">
                            <LeadAssurance text="No commitment — applying is free" />
                            <LeadAssurance text="Reviewed personally by King within 24h" />
                            <LeadAssurance text="If we're not the right fit, we'll tell you" />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <LeadForm />
                    </div>
                </div>
            </section>

            {/* ---------- FOOTER ---------- */}
            <footer className="bg-[var(--bg-deep)]">
                <div className="max-w-7xl mx-auto px-6 py-16">
                    <div className="grid md:grid-cols-12 gap-12 mb-12">
                        <div className="md:col-span-5">
                            <div className="font-serif text-2xl tracking-[0.32em] gold-gradient-text leading-none">NEXT LEVEL</div>
                            <div className="font-display text-xs tracking-[0.4em] text-[var(--text-muted)] mt-2 leading-none">BODY LAB</div>
                            <p className="text-sm text-[var(--text-muted)] mt-6 max-w-sm leading-relaxed">
                                The personal-coaching system engineered to take you from stuck to undeniable in 16 weeks.
                            </p>
                        </div>
                        <div className="md:col-span-3">
                            <p className="label-tiny mb-4">/ Site</p>
                            <ul className="space-y-2 text-sm">
                                <li><button onClick={() => scrollTo("about")} className="text-[var(--text-muted)] hover:text-[var(--gold-bright)]">About</button></li>
                                <li><button onClick={() => scrollTo("phases")} className="text-[var(--text-muted)] hover:text-[var(--gold-bright)]">The program</button></li>
                                <li><button onClick={() => scrollTo("pricing")} className="text-[var(--text-muted)] hover:text-[var(--gold-bright)]">Pricing</button></li>
                                <li><button onClick={() => scrollTo("apply")} className="text-[var(--text-muted)] hover:text-[var(--gold-bright)]">Apply</button></li>
                            </ul>
                        </div>
                        <div className="md:col-span-4">
                            <p className="label-tiny mb-4">/ Account</p>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/login" className="text-[var(--text-muted)] hover:text-[var(--gold-bright)]">Sign in</Link></li>
                                <li><Link to="/register" className="text-[var(--text-muted)] hover:text-[var(--gold-bright)]">Create account</Link></li>
                                <li><Link to="/apply-coach" className="text-[var(--text-muted)] hover:text-[var(--gold-bright)]">Coach with us</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="gold-rule mb-6" />
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs font-mono tracking-[0.18em] uppercase text-[var(--text-faint)]">
                        <p>© {new Date().getFullYear()} Next Level Body Lab. All rights reserved.</p>
                        <p>Coached by King Seidorb</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function Stat({ n, label }) {
    return (
        <div>
            <p className="font-serif text-4xl gold-gradient-text leading-none">{n}</p>
            <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[var(--text-muted)] mt-2">{label}</p>
        </div>
    );
}

function LeadAssurance({ text }) {
    return (
        <div className="flex items-center gap-3 text-[var(--text-muted)]">
            <Check size={14} className="text-[var(--gold-bright)] flex-shrink-0" />
            <span>{text}</span>
        </div>
    );
}

function LeadForm() {
    const [form, setForm] = useState({ name: "", email: "", goal: "", phone: "", plan_interest: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!form.name.trim() || !form.email.trim()) {
            setErr("Name and email are required.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/leads", { ...form, source: "landing" });
            setDone(true);
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail) || e2.message || "Could not submit.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="bg-[var(--surface)] border border-[var(--gold)] p-10 lg:p-12 text-center" data-testid="lead-form-success">
                <div className="w-14 h-14 border border-[var(--gold)] mx-auto mb-6 flex items-center justify-center">
                    <Check size={22} className="text-[var(--gold-bright)]" />
                </div>
                <p className="label-tiny mb-2 text-[var(--gold-bright)]">/ Application received</p>
                <h3 className="font-serif text-3xl lg:text-4xl mb-4">You&apos;re on the list.</h3>
                <p className="text-[var(--text-muted)] max-w-md mx-auto leading-relaxed">
                    King personally reviews every application and will be in touch within 24 hours.
                    Check your email for a confirmation — and meanwhile, browse the program above.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="bg-[var(--surface)] border border-[var(--border-bright)] p-8 lg:p-10 space-y-5" data-testid="lead-form">
            <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name *">
                    <input className="input-dark" value={form.name} onChange={setField("name")} required placeholder="Your full name" data-testid="lead-name" />
                </Field>
                <Field label="Email *">
                    <input type="email" className="input-dark" value={form.email} onChange={setField("email")} required placeholder="you@email.com" data-testid="lead-email" />
                </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Phone (optional)">
                    <input className="input-dark" value={form.phone} onChange={setField("phone")} placeholder="+1 (___) ___-____" data-testid="lead-phone" />
                </Field>
                <Field label="Interested in">
                    <select className="input-dark" value={form.plan_interest} onChange={setField("plan_interest")} data-testid="lead-plan">
                        <option value="">Not sure yet</option>
                        <option value="starter">Starter</option>
                        <option value="pro">Pro</option>
                        <option value="elite">Elite (1-on-1)</option>
                    </select>
                </Field>
            </div>
            <Field label="What's your #1 goal?">
                <input className="input-dark" value={form.goal} onChange={setField("goal")} placeholder="e.g. Drop 25 lbs, build muscle, prep for a wedding…" data-testid="lead-goal" />
            </Field>
            <Field label="Anything else? (optional)">
                <textarea rows={3} className="input-dark resize-none" value={form.message} onChange={setField("message")} placeholder="Tell us about your situation — current routine, time available, history…" data-testid="lead-message" />
            </Field>
            {err && <div className="text-sm text-[var(--danger)]" data-testid="lead-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="lead-submit">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Apply for coaching <ArrowRight size={14} /></>}
            </button>
            <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[var(--text-faint)] text-center">
                We respect your inbox. No spam, ever.
            </p>
        </form>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-tiny block mb-2">{label}</span>
            {children}
        </label>
    );
}
